import { useCallback, useState } from "react";
import { Link, useLocation, useSearch } from "wouter";
import DashboardLayout from "@/components/DashboardLayout";
import { PermissionGuard } from "@/components/PermissionGuard";
import { trpc } from "@/lib/trpc";
import { useContextoDeLista } from "@/features/assistente/contexto-de-tela";
import { useDebounce } from "@/hooks/useDebounce";
import { usePermissions } from "@/hooks/usePermissions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Package,
  Plus,
  Search,
  Pencil,
  Archive,
  GitCompareArrows,
  X,
} from "lucide-react";
import { toast } from "sonner";
import { toUserFriendlyError } from "@/lib/error-messages";
import ProductFormSheet from "@/components/Crm/ProductFormSheet";

function formatBRL(raw: string | number | null | undefined): string {
  const n = typeof raw === "string" ? Number(raw) : raw ?? 0;
  if (Number.isNaN(n)) return "R$ 0,00";
  return n.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

type ProductRow = {
  product: {
    id: number;
    sku: string | null;
    name: string;
    description: string | null;
    price: string;
    currency: string;
    imageUrl: string | null;
    categoryId: number | null;
    archived: boolean;
    attributes: any;
  };
  category: { id: number | null; name: string | null; color: string | null } | null;
  // Agregado das ofertas ativas (offerAggregate no servidor). 0 ofertas é o
  // estado normal de todo produto anterior à 0091.
  offerCount: number;
  offerMinPrice: string | null;
  offerMaxPrice: string | null;
};

type CategoryFilter = number | "all" | "none";

function readCategoryFromQuery(search: string): CategoryFilter {
  const params = new URLSearchParams(search.startsWith("?") ? search.slice(1) : search);
  const raw = params.get("categoryId");
  if (!raw) return "all";
  if (raw === "none") return "none";
  const n = Number(raw);
  // `?categoryId=abc` cai em "all" em vez de mandar NaN para o servidor.
  return Number.isFinite(n) && n > 0 ? n : "all";
}

function CrmProductsContent() {
  const [location, navigate] = useLocation();
  const querystring = useSearch();
  const { can } = usePermissions();
  const canManage = can("crm:products_manage");
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 300);

  // O filtro mora na URL, não em estado local — é o que faz o breadcrumb do
  // produto ("Catálogo > Categoria > Produto") poder voltar já filtrado, e o
  // que faz o Voltar do navegador preservar a categoria escolhida.
  // Mesmo padrão de Intelligence.tsx e Settings.tsx: derivar, não duplicar.
  const categoryId = readCategoryFromQuery(querystring);
  const setCategoryId = useCallback(
    (next: CategoryFilter) => {
      const pathname = location.split("?")[0];
      const params = new URLSearchParams(
        querystring.startsWith("?") ? querystring.slice(1) : querystring,
      );
      if (next === "all") params.delete("categoryId");
      else params.set("categoryId", String(next));
      const qs = params.toString();
      // replace: clicar em pastilha reescreve a entrada atual em vez de
      // empilhar uma por clique — senão o Voltar percorreria cada filtro.
      navigate(qs ? `${pathname}?${qs}` : pathname, { replace: true });
    },
    [location, querystring, navigate],
  );

  const [formOpen, setFormOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [compareIds, setCompareIds] = useState<number[]>([]);

  const categoriesQuery = trpc.crm.productCategories.list.useQuery({});

  const productsQuery = trpc.crm.products.list.useQuery({
    search: debouncedSearch || undefined,
    categoryId:
      categoryId === "all" ? undefined : categoryId === "none" ? null : categoryId,
    limit: 200,
  });

  useContextoDeLista({
    titulo: "Catálogo de produtos",
    itens: ((productsQuery.data as any)?.rows ?? []).map((r: any) => ({
      id: r.product?.id ?? r.id,
      nome: r.product?.name ?? r.name,
      preco: r.product?.price ?? r.price ?? null,
      categoria: r.category?.name ?? null,
    })),
  });

  const utils = trpc.useUtils();
  const archive = trpc.crm.products.archive.useMutation({
    onSuccess: () => {
      toast.success("Produto arquivado");
      utils.crm.products.list.invalidate();
    },
    onError: (e) => toast.error(toUserFriendlyError(e, "Erro ao arquivar produto")),
  });

  const rows = (productsQuery.data?.rows ?? []) as ProductRow[];
  const categories = categoriesQuery.data ?? [];

  function toggleCompare(id: number) {
    setCompareIds((prev) => {
      if (prev.includes(id)) return prev.filter((x) => x !== id);
      if (prev.length >= 4) {
        toast.info("Máximo 4 produtos na comparação");
        return prev;
      }
      return [...prev, id];
    });
  }

  // Contadores vêm do servidor porque precisam ignorar o filtro de categoria:
  // calculá-los a partir de `rows` (que já veio filtrado) zerava todas as
  // outras pastilhas assim que uma categoria era escolhida.
  const countsQuery = trpc.crm.products.categoryCounts.useQuery({
    search: debouncedSearch || undefined,
  });
  const counts = countsQuery.data;

  return (
    <div className="space-y-5">
      {/* ── Header ──────────────────────────────── */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <div className="h-10 w-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0">
            <Package className="h-5 w-5" />
          </div>
          <div className="min-w-0">
            <h1 className="text-xl sm:text-2xl font-bold tracking-tight truncate">Produtos</h1>
            <p className="text-xs sm:text-sm text-muted-foreground hidden sm:block">
              Catálogo do que você negocia nas oportunidades.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {compareIds.length >= 2 && (
            <Button
              variant="outline"
              onClick={() =>
                navigate(`/crm/products/compare?ids=${compareIds.join(",")}`)
              }
              className="shrink-0"
            >
              <GitCompareArrows className="h-4 w-4 sm:mr-1.5" />
              <span className="hidden sm:inline">
                Comparar ({compareIds.length})
              </span>
              <span className="sm:hidden ml-1.5">{compareIds.length}</span>
            </Button>
          )}
          {canManage && (
            <Button
              className="shrink-0"
              onClick={() => {
                setEditingId(null);
                setFormOpen(true);
              }}
            >
              <Plus className="h-4 w-4 sm:mr-1.5" />
              <span className="hidden sm:inline">Novo produto</span>
              <span className="sm:hidden ml-1.5">Novo</span>
            </Button>
          )}
        </div>
      </div>

      {/* ── Search + category chips ─────────────── */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="h-4 w-4 absolute left-2.5 top-2.5 text-muted-foreground" />
          <Input
            placeholder="Buscar por nome ou SKU..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-8"
          />
        </div>
      </div>

      {/* ── Category chips ──────────────────────── */}
      {(categories.length > 0 || (counts?.noCategory ?? 0) > 0) && (
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={() => setCategoryId("all")}
            className={`px-3 py-1 text-xs font-medium rounded-full border transition ${
              categoryId === "all"
                ? "bg-foreground text-background border-transparent"
                : "bg-background text-muted-foreground hover:text-foreground border-border"
            }`}
          >
            Todos
            <span className="ml-1.5 opacity-60">{counts?.total ?? 0}</span>
          </button>
          {categories.map((c: any) => {
            const count = counts?.byCategory[String(c.id)] ?? 0;
            const active = categoryId === c.id;
            return (
              <button
                key={c.id}
                onClick={() => setCategoryId(c.id)}
                className={`px-3 py-1 text-xs font-medium rounded-full border transition flex items-center gap-1.5 ${
                  active
                    ? "border-transparent shadow-sm"
                    : "bg-background text-muted-foreground hover:text-foreground border-border"
                }`}
                style={
                  active
                    ? {
                        backgroundColor: `${c.color}20`,
                        color: c.color,
                        boxShadow: `inset 0 0 0 1px ${c.color}40`,
                      }
                    : {}
                }
              >
                <span
                  className="h-2 w-2 rounded-full shrink-0"
                  style={{ backgroundColor: c.color }}
                />
                {c.name}
                <span className="opacity-60">{count}</span>
              </button>
            );
          })}
          {(counts?.noCategory ?? 0) > 0 && (
            <button
              onClick={() => setCategoryId("none")}
              className={`px-3 py-1 text-xs font-medium rounded-full border transition ${
                categoryId === "none"
                  ? "bg-foreground text-background border-transparent"
                  : "bg-background text-muted-foreground hover:text-foreground border-border"
              }`}
            >
              Sem categoria
              <span className="ml-1.5 opacity-60">{(counts?.noCategory ?? 0)}</span>
            </button>
          )}
        </div>
      )}

      {/* ── Compare bar ────────────────────────── */}
      {compareIds.length > 0 && (
        <Card className="bg-primary/5 border-primary/30">
          <CardContent className="p-3 flex items-center gap-2 flex-wrap">
            <GitCompareArrows className="h-4 w-4 text-primary shrink-0" />
            <span className="text-sm font-medium">
              Comparar {compareIds.length} produto{compareIds.length > 1 ? "s" : ""}
            </span>
            <span className="text-xs text-muted-foreground">
              (selecione até 4)
            </span>
            <div className="flex-1" />
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setCompareIds([])}
            >
              <X className="h-4 w-4 mr-1" />
              Limpar
            </Button>
            {compareIds.length >= 2 && (
              <Button
                size="sm"
                onClick={() =>
                  navigate(`/crm/products/compare?ids=${compareIds.join(",")}`)
                }
              >
                Abrir comparador
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {/* ── Grid ───────────────────────────────── */}
      {productsQuery.isLoading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
          {Array.from({ length: 8 }).map((_, i) => (
            <Skeleton key={i} className="h-56 w-full" />
          ))}
        </div>
      ) : rows.length === 0 ? (
        <Card>
          <CardContent className="py-16 text-center">
            <Package className="h-10 w-10 mx-auto mb-3 text-muted-foreground opacity-40" />
            <h3 className="font-semibold mb-1">
              {search ? "Nenhum produto encontrado" : "Nenhum produto ainda"}
            </h3>
            <p className="text-sm text-muted-foreground mb-4">
              {search
                ? "Tente outro termo ou remova os filtros."
                : "Cadastre o que você vende para vincular às oportunidades."}
            </p>
            {canManage && !search && (
              <Button
                onClick={() => {
                  setEditingId(null);
                  setFormOpen(true);
                }}
              >
                <Plus className="h-4 w-4 mr-1.5" />
                Criar primeiro produto
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
          {rows.map((row) => {
            const selected = compareIds.includes(row.product.id);
            return (
              <div
                key={row.product.id}
                className={`group relative rounded-xl border bg-card overflow-hidden transition hover:shadow-md ${
                  selected ? "ring-2 ring-primary" : ""
                }`}
              >
                {/* Imagem */}
                <Link
                  href={`/crm/products/${row.product.id}`}
                  className="block relative aspect-[4/3] bg-muted overflow-hidden"
                >
                  {row.product.imageUrl ? (
                    <img
                      src={row.product.imageUrl}
                      alt={row.product.name}
                      className="h-full w-full object-cover transition group-hover:scale-105"
                      onError={(e) => {
                        (e.target as HTMLImageElement).style.display = "none";
                      }}
                    />
                  ) : (
                    <div className="h-full w-full flex items-center justify-center text-muted-foreground">
                      <Package className="h-10 w-10 opacity-30" />
                    </div>
                  )}
                  {row.category && (
                    <Badge
                      className="absolute top-2 left-2 text-[10px] shadow-sm"
                      style={{
                        backgroundColor: row.category.color ?? "#64748b",
                        color: "white",
                      }}
                    >
                      {row.category.name}
                    </Badge>
                  )}

                  {/* Checkbox comparar */}
                  <div
                    className="absolute top-2 right-2"
                    onClick={(e) => {
                      e.preventDefault();
                      toggleCompare(row.product.id);
                    }}
                  >
                    <div
                      className={`h-7 w-7 rounded-full bg-background/90 backdrop-blur flex items-center justify-center shadow-sm transition ${
                        selected
                          ? "ring-2 ring-primary"
                          : "opacity-0 group-hover:opacity-100"
                      }`}
                      title="Comparar"
                    >
                      <Checkbox
                        checked={selected}
                        onCheckedChange={() => toggleCompare(row.product.id)}
                      />
                    </div>
                  </div>
                </Link>

                {/* Body */}
                <div className="p-3 space-y-1.5">
                  <Link
                    href={`/crm/products/${row.product.id}`}
                    className="block text-sm font-semibold hover:text-primary line-clamp-2 leading-tight"
                  >
                    {row.product.name}
                  </Link>
                  {row.product.sku && (
                    <div className="text-[11px] text-muted-foreground font-mono">
                      {row.product.sku}
                    </div>
                  )}
                  <div className="flex items-end justify-between gap-2 pt-1">
                    {/* Com ofertas, o preço do produto deixa de ser a resposta
                        certa — o card mostra o menor preço vigente. */}
                    <div>
                      <div className="text-xs text-muted-foreground">
                        {row.offerCount > 0 ? (
                          <>
                            A partir de{" "}
                            <span className="opacity-70">
                              · {row.offerCount}{" "}
                              {row.offerCount === 1 ? "oferta" : "ofertas"}
                            </span>
                          </>
                        ) : (
                          "Preço sugerido"
                        )}
                      </div>
                      <div className="text-base font-bold tabular-nums">
                        {formatBRL(
                          row.offerCount > 0 ? row.offerMinPrice : row.product.price,
                        )}
                      </div>
                    </div>
                    {canManage && (
                      <div className="flex opacity-0 group-hover:opacity-100 transition">
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-8 w-8"
                          title="Editar"
                          onClick={() => {
                            setEditingId(row.product.id);
                            setFormOpen(true);
                          }}
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-8 w-8"
                          title="Arquivar"
                          onClick={() => archive.mutate({ id: row.product.id })}
                        >
                          <Archive className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <ProductFormSheet
        open={formOpen}
        onOpenChange={setFormOpen}
        productId={editingId}
      />
    </div>
  );
}

export default function CrmProducts() {
  return (
    <DashboardLayout>
      <PermissionGuard permission="crm:products_view">
        <CrmProductsContent />
      </PermissionGuard>
    </DashboardLayout>
  );
}
